import type { SajuAnalysis } from '../types/input';
import type { SpouseRole } from '../types/compatibility';
import type { TenGod } from '../types/ten-gods';
import { getTenGod } from '../analysis/ten-gods';

const IDEAL: TenGod[] = ['정재', '정관'];
const GOOD: TenGod[] = ['식신', '정인', '편재', '편인'];
const CAUTION: TenGod[] = ['편관', '겁재', '상관'];

/**
 * 두 사주의 육친 역할을 분석한다.
 *
 * P1 일간이 P2에게 어떤 십신으로 작용하는지,
 * P2 일간이 P1에게 어떤 십신으로 작용하는지 양방향 판정.
 *
 * 자평 실무에서 강조하는 "서로에게 어떤 역할을 하는지"를 구조화.
 */
export function analyzeSpouseRole(
  analysis1: SajuAnalysis,
  analysis2: SajuAnalysis,
): SpouseRole {
  // P1 일간이 P2에게 무슨 십신인지 (P2 관점에서 P1을 봄)
  const p1RoleForP2 = getTenGod(analysis2.dayMaster, analysis1.dayMaster);
  // P2 일간이 P1에게 무슨 십신인지 (P1 관점에서 P2를 봄)
  const p2RoleForP1 = getTenGod(analysis1.dayMaster, analysis2.dayMaster);

  const harmony = evaluateRoleHarmony(p1RoleForP2, p2RoleForP1);

  const sameSex = analysis1.input.gender === analysis2.input.gender;
  // 동성 관계에서는 전통적 "배우자" 대신 "동반자" 용어로 서술한다.
  // (prefix의 "관계"와 어휘 중복을 피하기 위해 "관계"가 아닌 "동반자" 사용)
  const prefix = sameSex ? '동성 관계에서 ' : '';
  const roleNoun = sameSex ? '동반자' : '배우자';

  const roleLabel: Record<TenGod, string> = {
    '비견': '동지·라이벌', '겁재': '경쟁자',
    '식신': '편안한 보조자', '상관': '도전적 자극제',
    '편재': '실리적 파트너', '정재': `안정적 ${roleNoun}`,
    '편관': '강한 통제자', '정관': '듬직한 보호자',
    '편인': '독특한 조언자', '정인': '헌신적 후원자',
  };
  const storyLabel: Record<string, string> = {
    '이상적': `${prefix}P1과 P2가 서로에게 안정적인 ${roleNoun} 역할을 하는 이상적 구조`,
    '좋음': `${prefix}P1과 P2가 서로를 보조하며 자연스러운 역할 분담이 이루어지는 구조`,
    '보통': `${prefix}P1과 P2가 동등한 위치에서 경쟁과 협력이 공존하는 구조`,
    '주의': `${prefix}P1과 P2 사이에 통제·갈등의 긴장이 생기기 쉬운 구조`,
  };
  const description = `P1은 P2에게 ${roleLabel[p1RoleForP2]} 역할(${p1RoleForP2}), ` +
    `P2는 P1에게 ${roleLabel[p2RoleForP1]} 역할(${p2RoleForP1}). ` +
    storyLabel[harmony];

  return {
    person1RoleForPerson2: p1RoleForP2,
    person2RoleForPerson1: p2RoleForP1,
    roleHarmony: harmony,
    description,
  };
}

function evaluateRoleHarmony(
  role1: TenGod, role2: TenGod,
): SpouseRole['roleHarmony'] {
  const isIdeal1 = IDEAL.includes(role1);
  const isIdeal2 = IDEAL.includes(role2);
  const isGood1 = GOOD.includes(role1);
  const isGood2 = GOOD.includes(role2);
  const isCaution1 = CAUTION.includes(role1);
  const isCaution2 = CAUTION.includes(role2);

  if (isIdeal1 && isIdeal2) return '이상적';
  if (isIdeal1 || isIdeal2) return '좋음';
  if (isCaution1 && isCaution2) return '주의';
  if (isGood1 || isGood2) return '좋음';
  return '보통';
}
